import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, Animated, View, Pressable, ScrollView } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useLinkTo } from "@react-navigation/native";

import CustomText from "../../common/general/CustomText/CustomText";
import {
  MeDocument,
  useMeQuery,
  useUpdateNotificationsEmailSettingMutation,
  useMarkNotificationAsReadMutation,
  type Notification,
  NotificationEventType,
} from "../../../graphql/generated/graphql";
import { useNotification } from "../../../contexts/NotificationContext";
import CustomSwitch from "./../../common/general/CustomSwitch/CustomSwitch";

import { type AnnouncementGroup, getGroupFromCreatedDate, announcementGroups } from "../../../utils/notificationHelper";
import { formatDateWithTime } from "../../../utils/date";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { NotificationType } from "../../../graphql/generated/graphql";
import useFileFetch from "../../../hooks/useFileFetch";
import { useToast } from "../../../contexts/Toast/ToastProvider";

type UserNotification = Pick<
  Notification,
  "id" | "isRead" | "title" | "message" | "created" | "actionUrl" | "type" | "event"
>;

const threeMin = 60 * 1000 * 3;

function NotificationsSlideIn({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const linkTo = useLinkTo();

  const slideAnim = useRef(new Animated.Value(360)).current;

  useEffect(() => {
    if (visible) {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: 360,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, slideAnim]);

  const { notifications, refetch } = useNotification();
  const [toastedNotificationIds, setToastedNotificationIds] = useState<string[]>([]);

  useEffect(() => {
    if (notifications.length > 0) {
      const unToastedNotifications: UserNotification[] = [];
      const now = new Date().getTime();
      for (const notification of notifications) {
        if (now - new Date(notification.created as unknown as Date).getTime() <= threeMin) {
          if (!toastedNotificationIds.includes(notification.id)) {
            unToastedNotifications.push(notification as UserNotification);
          }
        }
      }

      unToastedNotifications.sort(
        (a, b) => new Date(a.created as Date).getTime() - new Date(b.created as Date).getTime()
      );

      // each untoasted notification is shown 3s between each other
      unToastedNotifications.forEach((notification, index) => {
        setTimeout(() => {
          showToast({
            message: notification.type === NotificationType.DownloadFile ? notification.message : notification.title,
            type: "info",
            actions:
              // add a download button next to the notification
              notification.type === NotificationType.DownloadFile
                ? [
                    {
                      actionText: "Download",
                      actionCallback: () => {
                        handleDownLoadFromLink(notification.actionUrl);
                      },
                    },
                  ]
                : [],
          });
        }, 3000 * index);
      });

      if (unToastedNotifications.length > 0) {
        setToastedNotificationIds((prev) => [...prev, ...unToastedNotifications.map((noti) => noti.id)]);
      }
    }
  }, [notifications, toastedNotificationIds]);

  const [markNotificationAsSeen] = useMarkNotificationAsReadMutation();
  const [updateNotificationEmailSettings] = useUpdateNotificationsEmailSettingMutation({
    refetchQueries: [
      {
        query: MeDocument,
      },
    ],
  });
  const { data: meData } = useMeQuery();

  const notificationsByGroups = useMemo(() => {
    const groups: { [key in AnnouncementGroup]?: UserNotification[] } = {};
    const sortedAnnouncements = [...notifications].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );
    for (const announcement of sortedAnnouncements) {
      const category = getGroupFromCreatedDate(new Date(announcement.created).getTime());
      if (groups[category] == null) {
        groups[category] = [];
      }
      groups[category].push(announcement as UserNotification);
    }

    return groups;
  }, [notifications]);

  const handleMarkNotificationAsSeen = useCallback(async (id: string) => {
    await markNotificationAsSeen({
      variables: {
        id,
      },
    });
    refetch();
  }, []);

  const handleUpdateNotificationEmailSetting = useCallback(async (newSetting: boolean) => {
    const response = await updateNotificationEmailSettings({
      variables: {
        skipAllNotificationEmail: newSetting,
      },
    });

    // react-native Alert does not work on web -> Todo: add custom Alert component with polifill for web platform
    if (response?.data?.updateNotificationsEmailSetting?.success === false) {
      const errorMessage = response?.data?.updateNotificationsEmailSetting?.message;
      if (errorMessage != null) {
        showToast({
          message: errorMessage,
          type: "error",
        });
      }
    }
  }, []);

  const { fetchAndOpenFile } = useFileFetch();

  const handleDownLoadFromLink = useCallback(async (link: string) => {
    if (link != null && link !== "") {
      await fetchAndOpenFile(link);
    }
  }, []);

  if (!visible) return null;

  return (
    <Animated.View style={[styles.mobile, { transform: [{ translateX: slideAnim }] }]}>
      <View style={styles.mobileContainer}>
        <View style={styles.topRow}>
          <CustomText
            size="m"
            weight="bold"
            useTranslationText={false}
            style={styles.headerText}
            text="Notifications"
          />
          <Pressable role="button" aria-label="Close notifications" style={styles.closeButton} onPress={onClose}>
            <Ionicons name="close" size={24} color="black" />
          </Pressable>
        </View>

        <ScrollView style={styles.contentContainer}>
          {announcementGroups.map((category) => {
            const groupNotifications = notificationsByGroups[category as AnnouncementGroup];
            if (groupNotifications == null) {
              return null;
            }
            return (
              <View key={category} style={styles.notificationsCategory}>
                <View style={styles.categoryTitleRow}>
                  <CustomText
                    size="m"
                    weight="normal"
                    useTranslationText={true}
                    style={styles.categoryTitle}
                    text={category}
                  />
                </View>
                <View style={styles.notificationsList}>
                  {groupNotifications.map((notification, index) => (
                    <View
                      key={notification.id}
                      style={[styles.notificationItem, !notification.isRead && styles.unreadNotification]}
                      onPointerDown={() => {
                        handleMarkNotificationAsSeen(notification.id);
                      }}>
                      <View style={styles.notificationReadIndicator}>
                        {!notification.isRead && <View style={styles.unreadDot} />}
                      </View>
                      <View style={styles.notificationContent}>
                        <View style={styles.notificationTitleRow}>
                          <CustomText
                            size="s"
                            weight="bold"
                            useTranslationText={false}
                            style={styles.notificationTitle}
                            text={notification.title}
                          />
                          <CustomText
                            size="xs"
                            weight="normal"
                            useTranslationText={false}
                            style={styles.timestamp}
                            text={formatDateWithTime(notification.created)}
                          />
                        </View>
                        <CustomText
                          size="s"
                          weight="normal"
                          useTranslationText={false}
                          style={styles.notificationMessage}
                          text={notification.message}
                        />
                        <View>
                          {notification.type === NotificationType.DownloadFile && (
                            <CustomButton
                              styleType="primary"
                              size="s"
                              variant="text"
                              text="Download"
                              onPress={() => {
                                handleDownLoadFromLink(notification.actionUrl);
                              }}
                            />
                          )}
                          {notification.type === NotificationType.Info &&
                            (notification.event?.eventType === NotificationEventType.AnnouncementPosted ||
                              notification.event?.eventType === NotificationEventType.AnnouncementUpdated) &&
                            notification.actionUrl != null && (
                              <CustomButton
                                styleType="primary"
                                size="s"
                                variant="text"
                                text="Go to Announcement Detail"
                                onPress={() => {
                                  linkTo(notification.actionUrl);
                                }}
                              />
                            )}
                          {notification.type === NotificationType.Info &&
                            (notification.event?.eventType === NotificationEventType.AssessmentSubmission ||
                              notification.event?.eventType === NotificationEventType.ProjectSubmission ||
                              notification.event?.eventType === NotificationEventType.AssignmentSubmission) && (
                              <CustomButton
                                styleType="primary"
                                size="s"
                                variant="text"
                                text="Go to Grading Center"
                                onPress={() => {
                                  window.location.href = "/admin/grading-center";
                                }}
                              />
                            )}
                        </View>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            );
          })}
          {notifications.length === 0 && (
            <View style={styles.emptyContainer}>
              <Ionicons name="notifications-outline" size={48} color="#9CA3AF" />
              <CustomText
                size="m"
                weight="medium"
                useTranslationText={false}
                style={styles.emptyText}
                text="No notifications yet"
              />
              <CustomText
                size="s"
                weight="normal"
                useTranslationText={false}
                style={styles.emptySubtext}
                text="We'll notify you when something important happens"
              />
            </View>
          )}
        </ScrollView>
        <View style={styles.notificationSettings}>
          <CustomText text={"Receive email notifications"} />
          <CustomSwitch
            value={!meData?.me?.skipAllNotificationEmail}
            onValueChange={() => handleUpdateNotificationEmailSetting(!meData?.me?.skipAllNotificationEmail)}
            trackColor={{ false: "#767577", true: "#81b0ff" }}
            thumbColor={meData?.me?.skipAllNotificationEmail ? "#ffffff" : "#f4f3f4"}
          />
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  mobile: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 360,
    zIndex: 100,
    backgroundColor: "#fff",
    // @ts-expect-error - works for web
    height: "100vh",
  },
  mobileContainer: {
    flex: 1,
    backgroundColor: "white",
    borderLeftWidth: 1,
    borderLeftColor: "#e0e0e0",
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  headerText: {
    fontSize: 18,
  },
  closeButton: {
    padding: 5,
  },
  contentContainer: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 100,
  },
  emptyText: {
    marginTop: 16,
    color: "#4B5563",
  },
  emptySubtext: {
    marginTop: 8,
    color: "#9CA3AF",
    textAlign: "center",
  },
  notificationsList: {
    flex: 1,
  },
  notificationItem: {
    paddingVertical: 12,
    flexDirection: "row",
    gap: 0,
    backgroundColor: "white",
  },
  unreadNotification: {
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
  },
  notificationTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  notificationTitle: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
    color: "#111827",
  },
  notificationMessage: {
    color: "#475467",
  },
  timestamp: {
    color: "#9CA3AF",
  },
  notificationsCategory: {
    marginBottom: 10,
  },
  categoryTitleRow: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  categoryTitle: {
    color: "#667085",
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 20,
  },
  notificationReadIndicator: {
    width: 40,
    alignItems: "center",
  },
  unreadDot: {
    borderRadius: 11,
    width: 11,
    height: 11,
    backgroundColor: "#0084FF",
    marginTop: 7,
  },
  notificationContent: {
    flex: 1,
    marginRight: 16,
    gap: 8,
  },
  notificationSettings: {
    display: "flex",
    flexDirection: "row",
    padding: 16,
    justifyContent: "space-between",
    alignItems: "center",
  },
});

export default NotificationsSlideIn;
